import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the previous location or default to home page
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/nomination-categories';
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    
    try {
      await login({ email, password });
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && 'response' in err && err.response && typeof err.response === 'object' && 'data' in err.response && err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data 
        ? String(err.response.data.message)
        : 'Failed to login. Please check your credentials.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section 
      className="min-h-screen flex"
      style={{
        minHeight: '100vh',
        display: 'flex'
      }}
    >
      {/* Left Side - Badge Image (Hidden on mobile, visible on large screens) */}
      <div 
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 items-center justify-center relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0f1b3c 0%, #1a2d5c 50%, #2563EB 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative circles */}
        <div 
          style={{
            position: 'absolute',
            top: '-10%',
            right: '-5%',
            width: '40%',
            height: '40%',
            borderRadius: '50%',
            background: 'rgba(249, 115, 22, 0.1)',
            filter: 'blur(60px)'
          }}
        ></div>
        <div 
          style={{
            position: 'absolute',
            bottom: '-10%',
            left: '-5%',
            width: '35%',
            height: '35%',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            filter: 'blur(60px)'
          }}
        ></div>
        
        <div 
          className="relative z-10 text-center px-12"
          style={{
            position: 'relative',
            zIndex: 10,
            textAlign: 'center',
            padding: '0 3rem'
          }}
        >
          <img 
            src="/src/assets/badge.png" 
            alt="TDA Award Badge" 
            className="w-full h-auto max-w-md mx-auto mb-8"
            style={{
              width: '100%',
              height: 'auto',
              maxWidth: '28rem',
              margin: '0 auto 2rem',
              filter: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3))'
            }}
          />
          <h2 
            className="text-3xl font-bold text-white mb-4"
            style={{
              fontSize: '1.875rem',
              fontWeight: '700',
              color: 'white',
              marginBottom: '1rem'
            }}
          >
            Tanzania Digital Awards
          </h2>
          <p 
            className="text-lg text-blue-100"
            style={{
              fontSize: '1.125rem',
              color: '#DBEAFE',
              lineHeight: '1.75'
            }}
          >
            Celebrating Excellence in Digital Innovation
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div 
        className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center bg-white px-4 sm:px-6 lg:px-12 xl:px-16 py-12"
        style={{
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '3rem 1rem'
        }}
      >
        <div 
          className="w-full max-w-md xl:max-w-lg"
          style={{
            width: '100%',
            maxWidth: '28rem'
          }}
        >
          {/* Mobile Logo */}
          <div 
            className="lg:hidden text-center mb-8"
            style={{
              textAlign: 'center',
              marginBottom: '2rem'
            }}
          >
            <img 
              src="/assets/logo/logo.png" 
              alt="TDA Logo" 
              className="h-12 w-auto mx-auto"
              style={{
                height: '3rem',
                width: 'auto',
                margin: '0 auto'
              }}
            />
          </div>
          
          {/* Header */}
          <div 
            className="mb-10"
            style={{
              marginBottom: '2.5rem'
            }}
          >
            <div 
              className="inline-block px-4 py-2 bg-blue-50 rounded-full mb-4"
              style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                backgroundColor: '#EFF6FF',
                borderRadius: '9999px',
                marginBottom: '1rem'
              }}
            >
              <span 
                className="text-sm font-semibold text-blue-900"
                style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#0f1b3c'
                }}
              >
                👋 Welcome Back
              </span>
            </div>
            <h1 
              className="text-3xl xl:text-4xl font-bold text-gray-900 tracking-tight mb-3"
              style={{
                fontSize: 'clamp(1.875rem, 4vw, 2.25rem)',
                fontWeight: '700',
                color: '#111827',
                letterSpacing: '-0.025em',
                marginBottom: '0.75rem'
              }}
            >
              Sign in to your account
            </h1>
            <p 
              className="text-base xl:text-lg text-gray-600"
              style={{
                fontSize: '1rem',
                color: '#4B5563',
                lineHeight: '1.75'
              }}
            >
              Enter your credentials to access your dashboard
            </p>
          </div>
          
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label 
                htmlFor="email" 
                className="block text-sm font-semibold text-gray-800 mb-2"
                style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#1F2937',
                  marginBottom: '0.5rem'
                }}
              >
                <i className="fa-solid fa-envelope mr-2" style={{ marginRight: '0.5rem', color: '#6B7280' }}></i>
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-5 py-4 bg-white border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 
                         focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900
                         transition-all duration-200 hover:border-gray-300 hover:shadow-sm"
                style={{
                  width: '100%',
                  padding: '1rem 1.25rem',
                  backgroundColor: 'white',
                  border: '2px solid #E5E7EB',
                  borderRadius: '0.75rem',
                  color: '#111827',
                  fontSize: '1rem',
                  transition: 'all 0.2s ease'
                }}
                placeholder="you@example.com"
              />
            </div>
            
            {/* Password */}
            <div>
              <div 
                className="flex justify-between items-center mb-2"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.5rem'
                }}
              >
                <label 
                  htmlFor="password" 
                  className="block text-sm font-semibold text-gray-800"
                  style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#1F2937'
                  }}
                >
                  <i className="fa-solid fa-lock mr-2" style={{ marginRight: '0.5rem', color: '#6B7280' }}></i>
                  Password
                </label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm font-medium text-blue-900 hover:text-orange-500 transition-colors duration-200"
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#0f1b3c',
                    textDecoration: 'none',
                    transition: 'color 0.2s ease'
                  }}
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-5 py-4 bg-white border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 
                         focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900
                         transition-all duration-200 hover:border-gray-300 hover:shadow-sm"
                style={{
                  width: '100%',
                  padding: '1rem 1.25rem',
                  backgroundColor: 'white',
                  border: '2px solid #E5E7EB',
                  borderRadius: '0.75rem',
                  color: '#111827',
                  fontSize: '1rem',
                  transition: 'all 0.2s ease'
                }}
                placeholder="Enter your password"
              />
            </div>
            
            {/* Remember me checkbox */}
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-5 w-5 text-[#0f1b3c] border-gray-300 rounded focus:ring-2 focus:ring-[#0f1b3c] cursor-pointer"
              />
              <label htmlFor="remember-me" className="ml-3 text-sm text-gray-700 cursor-pointer">
                Remember me for 30 days
              </label>
            </div>
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 px-6 bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-xl 
                       font-semibold text-base xl:text-lg shadow-lg hover:shadow-xl 
                       transform hover:-translate-y-0.5 transition-all duration-200
                       disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-lg
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900
                       mt-2 relative overflow-hidden group"
              style={{
                width: '100%',
                padding: '1rem 1.5rem',
                background: 'linear-gradient(135deg, #0f1b3c 0%, #1a2d5c 100%)',
                color: 'white',
                borderRadius: '0.75rem',
                fontWeight: '600',
                fontSize: '1rem',
                boxShadow: '0 10px 15px -3px rgba(30, 58, 138, 0.3), 0 4px 6px -2px rgba(30, 58, 138, 0.2)',
                transition: 'all 0.2s ease',
                position: 'relative',
                overflow: 'hidden',
                border: 'none',
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              <span 
                className="absolute inset-0 bg-gradient-to-r from-orange-500 to-orange-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(135deg, #ffba17 0%, #c2410c 100%)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease'
                }}
              ></span>
              <span className="relative z-10 flex items-center justify-center">
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </div>
                ) : (
                  <>
                    <i className="fa-solid fa-sign-in-alt mr-2" style={{ marginRight: '0.5rem' }}></i>
                    Sign In
                  </>
                )}
              </span>
            </button>
          </form>
          
          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gray-50 text-gray-500">
                New to TDA?
              </span>
            </div>
          </div>
          
          {/* Register Link */}
          <div 
            className="text-center"
            style={{
              textAlign: 'center'
            }}
          >
            <Link 
              to="/register" 
              className="inline-flex items-center justify-center w-full px-6 py-3.5 border-2 border-blue-900 
                       text-blue-900 font-semibold rounded-xl hover:bg-blue-900 hover:text-white 
                       transition-all duration-200 transform hover:-translate-y-0.5"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                padding: '0.875rem 1.5rem',
                border: '2px solid #0f1b3c',
                color: '#0f1b3c',
                fontWeight: '600',
                borderRadius: '0.75rem',
                textDecoration: 'none',
                transition: 'all 0.2s ease'
              }}
            >
              <i className="fa-solid fa-user-plus mr-2" style={{ marginRight: '0.5rem' }}></i>
              Create an Account
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;
